import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, UserRound } from "lucide-react";
import * as React from "react";

import { ApiError } from "@/api/client";
import { users } from "@/api/endpoints";
import type { AdminUser, UserStatus } from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import { useDebounced } from "@/hooks/useDebounced";
import { FilterBar, Field, PageHeader, Pagination } from "@/components/PageHeader";
import { Badge, toneFor } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Input, Label, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { formatDate, humanize } from "@/lib/utils";

const STATUS_OPTIONS = [
  { label: "Active", value: "ACTIVE" },
  { label: "Disabled", value: "DISABLED" },
  { label: "Pending", value: "PENDING" },
  { label: "Deletion pending", value: "DELETION_PENDING" },
  { label: "Deleted", value: "DELETED" },
];

export default function Users() {
  const { can } = useAuth();
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [selected, setSelected] = React.useState<AdminUser | null>(null);

  const q = useDebounced(search);

  React.useEffect(() => setPage(1), [q, status]);

  const query = useQuery({
    queryKey: ["users", { page, q, status }],
    queryFn: () =>
      users.list({
        page,
        page_size: 20,
        q: q || undefined,
        user_status: (status || undefined) as UserStatus | undefined,
      }),
  });

  return (
    <>
      <PageHeader
        title="Users"
        description="Farmers and service providers registered on the platform."
      />

      <FilterBar>
        <div className="relative min-w-[15rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by phone, email or name"
            className="pl-8"
            aria-label="Search users"
          />
        </div>
        <div className="w-44">
          <Select
            value={status}
            onValueChange={setStatus}
            options={STATUS_OPTIONS}
            placeholder="Any status"
            aria-label="Filter by status"
          />
        </div>
      </FilterBar>

      <Card className="overflow-hidden">
        {query.isLoading ? (
          <TableSkeleton />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data && query.data.items.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data.items.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">
                      {u.consumer_profile?.full_name ??
                        u.provider_profile?.business_name ??
                        u.display_name ??
                        "—"}
                    </TableCell>
                    <TableCell className="tabular text-muted-foreground">
                      {u.phone ?? "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {u.roles.map((r) => (
                          <Badge key={r} variant="outline">
                            {humanize(r)}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={toneFor(u.status)}>{humanize(u.status)}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {u.consumer_profile?.city ?? u.provider_profile?.city ?? "—"}
                    </TableCell>
                    <TableCell className="tabular text-muted-foreground">
                      {formatDate(u.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelected(u)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              page={query.data.page}
              pageSize={query.data.pageSize}
              total={query.data.total}
              onPageChange={setPage}
            />
          </>
        ) : (
          <EmptyState
            icon={UserRound}
            title="No users match those filters"
            description="Try a different search term or clear the status filter."
          />
        )}
      </Card>

      <UserDrawer
        user={selected}
        onClose={() => setSelected(null)}
        canWrite={can("user.write")}
      />
    </>
  );
}

function UserDrawer({
  user,
  onClose,
  canWrite,
}: {
  user: AdminUser | null;
  onClose: () => void;
  canWrite: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = React.useState("");

  React.useEffect(() => setReason(""), [user?.id]);

  const mutation = useMutation({
    mutationFn: ({ status }: { status: UserStatus }) =>
      users.setStatus(user!.id, status, reason || undefined),
    onSuccess: (_data, variables) => {
      toast.success(
        variables.status === "ACTIVE" ? "User enabled" : "User disabled",
        user?.phone ?? undefined,
      );
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (error) =>
      toast.error(
        "Could not update the user",
        error instanceof ApiError ? error.message : undefined,
      ),
  });

  if (!user) return null;

  const disabling = user.status === "ACTIVE";
  // A deleted account's data is gone; the API refuses to reactivate it.
  const actionable = canWrite && user.status !== "DELETED";
  const actionLabel = disabling
    ? "Disable account"
    : user.status === "DELETION_PENDING"
      ? "Cancel deletion"
      : "Enable account";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent side="right">
        <DialogHeader>
          <DialogTitle>
            {user.consumer_profile?.full_name ??
              user.provider_profile?.business_name ??
              user.display_name ??
              "User"}
          </DialogTitle>
          <DialogDescription>
            Everything support can see about this account.
          </DialogDescription>
        </DialogHeader>

        <dl className="grid grid-cols-2 gap-4">
          <Field label="Phone">
            <span className="tabular">{user.phone ?? "—"}</span>
          </Field>
          <Field label="Email">{user.email ?? "—"}</Field>
          <Field label="Status">
            <Badge variant={toneFor(user.status)}>{humanize(user.status)}</Badge>
          </Field>
          <Field label="Joined">{formatDate(user.created_at)}</Field>
          <Field label="Roles">{user.roles.map(humanize).join(", ") || "—"}</Field>
          <Field label="User ID">
            <span className="tabular break-all text-xs text-muted-foreground">
              {user.id}
            </span>
          </Field>
        </dl>

        {user.consumer_profile && (
          <>
            <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Farmer profile
            </h3>
            <dl className="grid grid-cols-2 gap-4">
              <Field label="Full name">{user.consumer_profile.full_name}</Field>
              <Field label="City">{user.consumer_profile.city ?? "—"}</Field>
              <Field label="State">{user.consumer_profile.state ?? "—"}</Field>
              <Field label="PIN code">
                {user.consumer_profile.postal_code ?? "—"}
              </Field>
              <div className="col-span-2">
                <Field label="Address">
                  {user.consumer_profile.default_address ?? "—"}
                </Field>
              </div>
            </dl>
          </>
        )}

        {user.provider_profile && (
          <>
            <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Provider profile
            </h3>
            <dl className="grid grid-cols-2 gap-4">
              <Field label="Business">{user.provider_profile.business_name}</Field>
              <Field label="Verification">
                <Badge variant={toneFor(user.provider_profile.verification_status)}>
                  {humanize(user.provider_profile.verification_status)}
                </Badge>
              </Field>
              <Field label="Rating">
                <span className="tabular">
                  {user.provider_profile.rating_average.toFixed(1)}
                </span>
              </Field>
              <Field label="Online">
                {user.provider_profile.is_online ? "Yes" : "No"}
              </Field>
            </dl>
          </>
        )}

        {actionable && (
          <>
            <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Account action
            </h3>
            <div className="space-y-2">
              <Label htmlFor="reason">
                Reason {disabling && <span className="text-muted-foreground">(recorded in the audit log)</span>}
              </Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={
                  disabling
                    ? "e.g. Repeated fraudulent bookings"
                    : "e.g. Appeal reviewed, account restored"
                }
                rows={3}
              />
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {actionable && (
            <Button
              variant={disabling ? "destructive" : "default"}
              disabled={mutation.isPending}
              onClick={() =>
                mutation.mutate({ status: disabling ? "DISABLED" : "ACTIVE" })
              }
            >
              {actionLabel}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
